import { useCallback, useEffect, useMemo, useState } from 'react';
import { markChatAsSeen, markChatAsUnread } from '../services/chat';
import { conversationRowKey, type ConversationRowRef } from '../services/conversations';
import type { ToastType } from '../providers/ToastProvider';

type SelectableRow = ConversationRowRef & { unread: boolean };

export function useMultiSelect(
    displayedItems: SelectableRow[],
    conversations: SelectableRow[],
    refreshConversations: () => Promise<void>,
    showToast: (msg: string, type: ToastType) => void,
    resetDeps: unknown[]
) {
    const [selectMode, setSelectMode] = useState(false);
    const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
    const [isBulkProcessing, setIsBulkProcessing] = useState(false);

    const exitSelectMode = useCallback(() => {
        setSelectMode(false);
        setSelectedKeys(new Set());
    }, []);

    // DataTable's checkbox column already stops the click reaching the row, so
    // this takes the row itself rather than an event.
    const toggleRow = useCallback((row: ConversationRowRef) => {
        const key = conversationRowKey(row);
        setSelectedKeys((prev) => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    }, []);

    const toggleSelectAll = useCallback(() => {
        setSelectedKeys((prev) => {
            const allDisplayed = displayedItems.map(conversationRowKey);
            const allSelected = allDisplayed.every((key) => prev.has(key));
            if (allSelected) return new Set();
            return new Set(allDisplayed);
        });
    }, [displayedItems]);

    // Exit select mode when filters/page change

    useEffect(() => {
        exitSelectMode();
    }, resetDeps);

    const selectedRows = useMemo(
        () => conversations.filter((c) => selectedKeys.has(conversationRowKey(c))),
        [conversations, selectedKeys]
    );

    const selectionType = useMemo(() => {
        if (selectedRows.length === 0) return 'empty' as const;
        const hasUnread = selectedRows.some((r) => r.unread);
        const hasRead = selectedRows.some((r) => !r.unread);
        if (hasUnread && hasRead) return 'mixed' as const;
        return hasUnread ? ('unread' as const) : ('read' as const);
    }, [selectedRows]);

    const runBulk = useCallback(
        async (
            action: (row: ConversationRowRef) => Promise<void>,
            failureMessage: string
        ): Promise<void> => {
            if (selectedRows.length === 0) return;
            setIsBulkProcessing(true);
            try {
                const results = await Promise.allSettled(selectedRows.map(action));
                const failed = results.filter((r) => r.status === 'rejected').length;
                await refreshConversations();
                exitSelectMode();
                if (failed > 0)
                    showToast(
                        `${failed} conversation${failed === 1 ? '' : 's'} failed to update`,
                        'error'
                    );
            } catch {
                showToast(failureMessage, 'error');
            } finally {
                setIsBulkProcessing(false);
            }
        },
        [selectedRows, refreshConversations, showToast, exitSelectMode]
    );

    const handleBulkMarkAsDone = useCallback(
        () => runBulk((row) => markChatAsSeen(row.patientId, row.status), 'Failed to mark as read'),
        [runBulk]
    );

    const handleBulkMarkAsUnread = useCallback(
        () =>
            runBulk(
                (row) => markChatAsUnread(row.patientId, row.status),
                'Failed to mark as unread'
            ),
        [runBulk]
    );

    return {
        selectMode,
        setSelectMode,
        selectedKeys,
        isBulkProcessing,
        exitSelectMode,
        toggleRow,
        toggleSelectAll,
        selectionType,
        handleBulkMarkAsDone,
        handleBulkMarkAsUnread
    };
}
